import React, { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { WappContext } from 'wapplr-react/dist/common/Wapp'

import AppContext from '../App/context'

import { consentKeys } from './utils'

const Lazy = lazy(() => import('./sync').catch((error) => {
    console.log('[APP] Component Failed Loading:', error)
    return { default: () => <div /> }
}))

export default React.forwardRef((props, ref) => {

    const {
        cookiesPropertyName = 'acceptCookies',
        dataLayer = 'dataLayer'
    } = props

    const context = useContext(WappContext)
    const appContext = useContext(AppContext)
    const { wapp, req, res } = context

    function getGranted() {
        const lastConsent = getLastConsentState()
        if (lastConsent?.values) {
            return {
                ...consentKeys.reduce((o, key) => {
                    o[key] = lastConsent.values[key] === 'granted' ? 'granted' : 'denied'
                    return o
                }, {})
            }
        }
        return {
            ...consentKeys.reduce((o, key) => {
                if (key === 'functionality_storage' || key === 'security_storage') {
                    o[key] = 'granted'
                } else {
                    o[key] = 'denied'
                }
                return o
            }, {})
        }
    }

    function getLastConsentState() {
        const dl = (typeof window !== 'undefined' && window[dataLayer]) || []
        if (!Array.isArray(dl)) return null

        let last = null

        for (let i = 0; i < dl.length; i++) {
            const item = dl[i]

            if (
                Array.isArray(item) &&
                item[0] === 'consent' &&
                (item[1] === 'default' || item[1] === 'update') &&
                typeof item[2] === 'object' &&
                item[2] !== null
            ) {
                last = {
                    type: item[1],
                    values: { ...item[2] },
                    index: i,
                    source: 'gtag'
                }
                continue
            }

            if (item && typeof item === 'object') {
                const evt = item.event
                const settings =
                    item['consent'] ||
                    item['gtm.consentSettings'] ||
                    (item.gtm && item.gtm.consentSettings) ||
                    Object.keys(item).includes(consentKeys[0])

                if (
                    (evt?.match('update_consent') || evt?.match('init_consent')) &&
                    settings &&
                    typeof settings === 'object'
                ) {
                    last = {
                        type: evt.match('init_consent') ? 'default' : 'update',
                        values: { ...settings },
                        index: i,
                        source: 'gtm'
                    }
                }
            }
        }

        if (!last) return null

        const cleaned = {}
        for (const k of consentKeys) {
            if (k in last.values) cleaned[k] = last.values[k]
        }
        last.values = cleaned

        return last
    }

    function setGtagConsent(state) {
        const next = consentKeys.reduce((o, key) => {
            if (key === 'functionality_storage' || key === 'security_storage') {
                o[key] = 'granted'
            } else if (key === 'analytics_storage') {
                o[key] = state.analytics ? 'granted' : 'denied'
            } else {
                o[key] = state.marketing ? 'granted' : 'denied'
            }
            return o
        }, {})

        const isDifferent = consentKeys.some((k) => granted.current[k] !== next[k])

        if (isDifferent) {
            appContext.analytics.consent({
                event: 'update_consent',
                consent: {
                    ...next
                },
                req,
                res
            })
            granted.current = getGranted()
            if (wapp.globals.DEV) {
                console.log('[APP] Cookie consent update', granted.current)
            }
        }
    }

    const granted = useRef(getGranted())

    if (wapp.target === 'web') {
        useLayoutEffect(() => {
            const { storage } = appContext
            const localStorageKey = cookiesPropertyName + '-new'
            const initialAcceptCookies = storage()[localStorageKey] || {
                required: true,
                analytics: false,
                marketing: false
            }
            setGtagConsent(initialAcceptCookies)
        }, [])
    }

    const [userInteract, setUserInteract] = useState(false)

    useEffect(() => {

        function on(e) {
            if (e.isTrusted) {
                if (!userInteract) {
                    setUserInteract(true)
                }
                removeListeners()
            }
        }

        function removeListeners() {
            window.removeEventListener('touchstart', on)
            window.removeEventListener('mousemove', on)
        }

        window.addEventListener('touchstart', on)
        window.addEventListener('mousemove', on)

        return () => {
            removeListeners()
        }

    }, [])

    return (
        <Suspense fallback={<div></div>}>
            {userInteract ? <Lazy {...props} ref={ref} /> : null}
        </Suspense>
    )
})
